import React, { useCallback, useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import {
  DashboardAttentionEngagementClient,
  PortalPatient,
} from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  ActionsColumnHeader,
  BooleanIndicator,
  PortalDataTable,
  PortalTableColumn,
  StatusPill,
  SudsPill,
  ValuePillList,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  ClientListFilters,
  ClientSignalFilter,
  initialClientSignalFilter,
} from "../components/ClientListFilters";
import { EngagementSignal } from "../components/EngagementSignal";
import {
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { RowActionMenu } from "../components/HomeworkControls";
import {
  TableColumnsButton,
  usePortalTablePreferences,
} from "../components/TableColumnPreferences";
import { addClientPath, editClientPath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

type ClientFilter = "activated" | "all";

export function MyClientsPage({ clinicianCode }: { clinicianCode: string }) {
  const [filter, setFilter] = useState<ClientFilter>("activated");
  const [search, setSearch] = useState("");
  const [signalFilter, setSignalFilter] = useState<ClientSignalFilter>(
    initialClientSignalFilter,
  );
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getMyClients(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const engagementRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getDashboardAttention(clinicianCode, signal),
    [clinicianCode],
  );
  const engagementState = usePortalData(engagementRequest);
  const engagementData =
    engagementState.status === "success" ||
    engagementState.status === "refreshing"
      ? engagementState.data
      : undefined;
  const engagementByClientId = useMemo(
    () =>
      new Map<string, DashboardAttentionEngagementClient>(
        (engagementData?.engagementClients ?? []).map((client) => [
          client.clientId,
          client,
        ]),
      ),
    [engagementData],
  );

  const columns = useMemo<readonly PortalTableColumn<PortalPatient>[]>(
    () => [
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        headerLabel: "Actions",
        initialWidth: 60,
        minWidth: 60,
        resizable: false,
        cellClassName: "cp-table-cell--compact",
        render: (patient) => (
          <RowActionMenu
            clinicianCode={clinicianCode}
            editPath={editClientPath(patient.id)}
            activityName={patient.nickname || patient.accessCode || "client"}
            canEdit
            showDuplicate={false}
            showDelete={false}
          />
        ),
      },
      {
        key: "signal",
        header: "Signal",
        headerLabel: "Signal",
        initialWidth: 76,
        minWidth: 76,
        resizable: false,
        cellClassName: "cp-table-cell--signal",
        render: (patient) => (
          <EngagementSignal
            client={engagementByClientId.get(patient.id)}
            unavailableLabel={
              patient.status.trim().toLowerCase() === "activated"
                ? "Engagement signal unavailable"
                : "Not evaluated — client is not activated"
            }
          />
        ),
      },
      {
        key: "accessCode",
        header: "Access Code",
        initialWidth: 110,
        minWidth: 110,
        render: (patient) => (
          <span className="cp-homework-name">{patient.accessCode}</span>
        ),
      },
      {
        key: "nickname",
        header: "Nickname",
        initialWidth: 150,
        minWidth: 120,
        render: (patient) => patient.nickname,
      },
      {
        key: "status",
        header: "Status",
        initialWidth: 110,
        minWidth: 110,
        render: (patient) => <StatusPill status={patient.status} />,
      },
      {
        key: "clinicalSummary",
        header: "Clinical Summary",
        initialWidth: 300,
        minWidth: 180,
        cellClassName: "cp-table-cell--truncate",
        render: (patient) =>
          patient.clinicalSummary || (
            <span className="cp-empty-value">Not provided</span>
          ),
      },
      {
        key: "highSudsThreshold",
        header: "High SUDS",
        initialWidth: 96,
        minWidth: 96,
        render: (patient) => <SudsPill value={patient.highSudsThreshold} />,
      },
      {
        key: "selfInitiatedPermitted",
        header: "Self-initiated",
        initialWidth: 120,
        minWidth: 120,
        render: (patient) => (
          <BooleanIndicator value={patient.selfInitiatedPermitted} />
        ),
      },
      {
        key: "copingStrategies",
        header: "Coping Strategies",
        initialWidth: 260,
        minWidth: 180,
        render: (patient) => (
          <ValuePillList values={patient.copingStrategies} kind="coping" />
        ),
      },
      {
        key: "funActivities",
        header: "Fun Activities",
        initialWidth: 240,
        minWidth: 180,
        render: (patient) => (
          <ValuePillList values={patient.funActivities} kind="fun" />
        ),
      },
    ],
    [clinicianCode, engagementByClientId],
  );
  const tablePreference = usePortalTablePreferences({
    clinicianCode,
    tableKey: "my_clients",
    columns,
    rules: [
      {
        key: "actions",
        hideable: false,
        movable: false,
        position: "left",
      },
      { key: "accessCode", hideable: false },
    ],
  });

  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const statusScopedPatients = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.patients;
    return data.patients.filter(
      (patient) => patient.status.trim().toLowerCase() === "activated",
    );
  }, [data, filter]);
  const visiblePatients = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return statusScopedPatients.filter((patient) => {
      if (
        signalFilter &&
        engagementByClientId.get(patient.id)?.engagementStatus !== signalFilter
      ) {
        return false;
      }
      if (!query) return true;
      return [patient.accessCode, patient.nickname, patient.clinicalSummary]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [engagementByClientId, search, signalFilter, statusScopedPatients]);
  const secondaryFiltersActive = Boolean(search.trim() || signalFilter);
  const refreshing =
    state.status === "refreshing" || engagementState.status === "refreshing";
  const refresh = () => {
    state.refresh();
    engagementState.refresh();
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="clients"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="My Clients"
        clinicianName={data?.therapist?.therapistName}
      >
        <div className="cp-filter-group" aria-label="Client status filter">
          <button
            type="button"
            aria-pressed={filter === "activated"}
            onClick={() => setFilter("activated")}
          >
            Activated Clients
          </button>
          <button
            type="button"
            aria-pressed={filter === "all"}
            onClick={() => setFilter("all")}
          >
            All Clients
          </button>
        </div>
      </PortalHeader>
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        {state.status === "loading" ? (
          <LoadingState label="Loading clients" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data ? (
          <>
            <div className="cp-content-header cp-content-header--count-only">
              <strong className="cp-record-count">
                {secondaryFiltersActive
                  ? `${visiblePatients.length} of ${statusScopedPatients.length}`
                  : visiblePatients.length}{" "}
                {statusScopedPatients.length === 1 ? "client" : "clients"}
              </strong>
            </div>
            <div className="cp-list-toolbar cp-list-toolbar--clients">
              <ClientListFilters
                idPrefix="my-clients"
                search={search}
                signal={signalFilter}
                onSearchChange={setSearch}
                onSignalChange={setSignalFilter}
              />
              <div className="cp-list-toolbar__actions">
                <TableColumnsButton preference={tablePreference} />
                <button
                  type="button"
                  className="cp-button cp-button--secondary cp-button--icon-only"
                  aria-label={refreshing ? "Refreshing…" : "Refresh"}
                  title={refreshing ? "Refreshing…" : "Refresh"}
                  data-loading={refreshing || undefined}
                  onClick={refresh}
                  disabled={refreshing}
                >
                  <RefreshCw aria-hidden="true" />
                </button>
                <PortalLink
                  className="cp-button cp-button--primary"
                  path={addClientPath}
                  clinicianCode={clinicianCode}
                >
                  <Plus aria-hidden="true" />
                  Add Client
                </PortalLink>
              </div>
            </div>
            {visiblePatients.length > 0 ? (
              <PortalDataTable
                caption="My Clients"
                columns={tablePreference.visibleColumns}
                preferredColumnWidths={tablePreference.columnWidths}
                onColumnWidthsChange={tablePreference.updateColumnWidths}
                records={visiblePatients}
                getRecordKey={(patient) => patient.id}
                getRecordOpenLabel={(patient) =>
                  `Open ${patient.nickname || patient.accessCode || "client"}`
                }
                onRecordOpen={(patient) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      `/clinician-portal/clients/${encodeURIComponent(
                        patient.id,
                      )}/profile`,
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <EmptyState
                title={
                  secondaryFiltersActive
                    ? "No matching clients"
                    : filter === "activated"
                      ? "No activated clients"
                      : "No clients"
                }
                description={
                  secondaryFiltersActive
                    ? "No clients match the current Search and Signal filters."
                    : filter === "activated"
                      ? "There are no activated clients to display. Try All Clients."
                      : "There are no linked clients to display."
                }
              />
            )}
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
